import React, { useState } from 'react';
import SEO from './SEO';
import { Send, Mail, Phone, User } from 'lucide-react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { useSound } from '../hooks/useSound';

interface ContactProps {
  isPage?: boolean;
}

const Contact: React.FC<ContactProps> = ({ isPage = false }) => {
  const { playClick } = useSound();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (!formData.service) newErrors.service = 'Please select a service';

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // EmailJS configuration from environment variables
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
      const confirmationTemplateId = import.meta.env.VITE_EMAILJS_CONFIRMATION_TEMPLATE_ID;
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

      // Template parameters for admin email
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        to_email: 'nuviosites@gmail.com'
      };

      // Send email to admin (nuviosites@gmail.com)
      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      // Send confirmation email to user
      const confirmationParams = {
        to_name: formData.name,
        to_email: formData.email,
        service: formData.service
      };

      await emailjs.send(serviceId, confirmationTemplateId, confirmationParams, publicKey);

      // Success - reset form and show success message
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setIsSubmitting(false);
      setSubmitError('Failed to send message. Please try again or contact us directly at nuviosites@gmail.com');

      setTimeout(() => {
        setSubmitError('');
      }, 7000);
    }
  };


  return (
    <div className="bg-[#F5F5F5] text-black min-h-screen">
      {isPage && (
        <SEO
          title="Contact Us | Nuviosites | TDMA"
          description="Get in touch with Nuviosites | TDMA for web development, AI solutions, and digital transformation. Let's discuss your project."
          url="https://tdma.one/contact"
        />
      )}
      {/* Main Container with responsive padding */}
      <div className="xl:px-[38px] lg:px-[25px] md:px-5 px-4 mx-auto">
        {/* Header Section */}
        <div className="flex flex-col gap-5 pt-24 pb-8 md:pt-32 md:pb-12 lg:py-16">
          {isPage ? (
            <h1 className="xl:text-[88px] lg:text-[68px] md:text-[58px] text-[42px] font-normal text-black tracking-[-0.04em] leading-[1.05] mt-8 md:mt-12 lg:mt-16 xl:mt-20">
              Let's get in<br className="block" /> touch
            </h1>
          ) : (
            <h2 className="xl:text-[88px] lg:text-[68px] md:text-[58px] text-[42px] font-normal text-black tracking-[-0.04em] leading-[1.05] mt-8 md:mt-12 lg:mt-16 xl:mt-20">
              Let's get in<br className="block" /> touch
            </h2>
          )}
          <div className="flex flex-col md:flex-row gap-6 md:gap-10 lg:gap-16 xl:gap-20 max-w-4xl">
            <div className="flex flex-col">
              <h2 className="text-black text-base md:text-lg font-medium">Located</h2>
              <div className="flex flex-col text-base md:text-lg mt-1">
                <a className="text-gray-600 hover:text-black transition-colors" href="#">
                  Coimbatore
                </a>
                <a className="text-gray-600 hover:text-black transition-colors" href="#">
                  India
                </a>
              </div>
            </div>
            <div className="flex flex-col">
              <h2 className="text-black text-base md:text-lg font-medium">Contact</h2>
              <div className="flex flex-col text-base md:text-lg mt-1">
                <a className="text-gray-600 hover:text-black transition-colors" href="#">
                  Nuviosites<br />nuviosites@gmail.com
                </a>
              </div>
            </div>
            <div className="flex flex-col">
              <h2 className="text-black text-base md:text-lg font-medium">Follow</h2>
              <div className="flex flex-col text-base md:text-lg mt-1">
                <a target="_blank" className="text-gray-600 hover:text-black transition-colors" href="https://www.instagram.com/nuviosites">
                  Instagram
                </a>
                <a target="_blank" className="text-gray-600 hover:text-black transition-colors" href="https://www.linkedin.com/in/nuviosites">
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Follow Card Section - Responsive height and layout */}
        <div className="mt-8 md:mt-12 lg:mt-16">
          <div className="relative rounded-xl md:rounded-2xl xl:rounded-3xl transition-all duration-500 h-[300px] sm:h-[400px] md:h-[500px] lg:h-[599px] w-full overflow-hidden">
            {/* Background Image */}
            <div className="absolute inset-0">
              <img
                src="/image.png"
                alt="Billboard Background"
                className="w-full h-full object-cover"
              />
              {/* Optional overlay for better text visibility if needed */}
              <div className="absolute inset-0 bg-black/10" />
            </div>

            {/* Mobile Mockup */}
            <div className="absolute -left-12 sm:-left-20 md:-left-24 lg:right-0 bottom-0 flex justify-end items-end pointer-events-none w-full h-full">
              <img
                alt="Mobile Mockup"
                loading="lazy"
                width="820"
                height="815"
                decoding="async"
                className="w-[400px] sm:w-[600px] md:w-[820px] lg:w-[920px] translate-x-5 translate-y-0 object-cover object-bottom"
                src="/Free_Realistic_iPhone_16_Pro_in_Hand_Mockup.svg"
              />
            </div>

            {/* Content Overlay */}
            <div className="relative z-10 h-full flex flex-col justify-between p-4 sm:p-6 md:p-8 lg:p-10">
              <div>
                {/* <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl max-w-[60%] sm:max-w-[50%] md:max-w-[400px] lg:max-w-[470px] leading-tight tracking-[-0.05em] font-normal text-white drop-shadow-lg">
                  Follow us on<br />
                  <span className="opacity-90 font-light">Instagram</span><br />
                  <span className="opacity-90 font-light">LinkedIn</span>
                </h3> */}
              </div>
            </div>
          </div>
        </div>

        {/* Integrated Contact Form Section */}
        <section className="mt-16 md:mt-20 lg:mt-24 xl:mt-32 py-12 md:py-16 lg:py-20 xl:py-24">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light tracking-[-0.04em] mb-4 md:mb-6">
                Get in Touch
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-black/70 font-light">
                Let's discuss how we can help bring your vision to life
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {submitSuccess ? (
                <div className="text-center py-16 md:py-20 bg-black/5 rounded-2xl md:rounded-3xl">
                  <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-green-500/10 text-green-600 mb-4 md:mb-6">
                    <svg className="w-6 h-6 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light mb-3 md:mb-4">Message Sent Successfully!</h3>
                  <p className="text-black/70 text-sm md:text-lg">
                    Thank you for reaching out. We'll get back to you within 24-48 hours.
                  </p>
                </div>
              ) : (
                <>
                  {submitError && (
                    <div className="mb-6 p-4 md:p-5 bg-red-50 border-2 border-red-200 rounded-xl md:rounded-2xl">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 md:w-6 md:h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-red-800 text-sm md:text-base">{submitError}</p>
                      </div>
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm md:text-base font-medium text-black/70 mb-2 md:mb-3">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={`w-full px-4 md:px-6 py-3 md:py-4 bg-black/5 border-2 ${errors.name ? 'border-red-500' : 'border-black/10'} rounded-xl md:rounded-2xl focus:outline-none focus:border-[#FF4E45] transition-all text-base md:text-lg`}
                          placeholder="John Doe"
                        />
                        {errors.name && <p className="mt-1 md:mt-2 text-xs md:text-sm text-red-500">{errors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm md:text-base font-medium text-black/70 mb-2 md:mb-3">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full px-4 md:px-6 py-3 md:py-4 bg-black/5 border-2 ${errors.email ? 'border-red-500' : 'border-black/10'} rounded-xl md:rounded-2xl focus:outline-none focus:border-[#FF4E45] transition-all text-base md:text-lg`}
                          placeholder="john@example.com"
                        />
                        {errors.email && <p className="mt-1 md:mt-2 text-xs md:text-sm text-red-500">{errors.email}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm md:text-base font-medium text-black/70 mb-2 md:mb-3">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full px-4 md:px-6 py-3 md:py-4 bg-black/5 border-2 ${errors.phone ? 'border-red-500' : 'border-black/10'} rounded-xl md:rounded-2xl focus:outline-none focus:border-[#FF4E45] transition-all text-base md:text-lg`}
                          placeholder="+91 98765 43210"
                        />
                        {errors.phone && <p className="mt-1 md:mt-2 text-xs md:text-sm text-red-500">{errors.phone}</p>}
                      </div>
                      <div>
                        <label htmlFor="service" className="block text-sm md:text-base font-medium text-black/70 mb-2 md:mb-3">
                          Service Needed *
                        </label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className={`w-full px-4 md:px-6 py-3 md:py-4 bg-black/5 border-2 ${errors.service ? 'border-red-500' : 'border-black/10'} rounded-xl md:rounded-2xl focus:outline-none focus:border-[#FF4E45] transition-all text-base md:text-lg`}
                        >
                          <option value="">Select a service</option>
                          <option value="web-development">Web Development</option>
                          <option value="mobile-app">Mobile App Development</option>
                          <option value="ui-ux-design">UI/UX Design</option>
                          <option value="seo">SEO Optimization</option>
                          <option value="branding">Web Redesign</option>
                          <option value="other">Other</option>
                        </select>
                        {errors.service && <p className="mt-1 md:mt-2 text-xs md:text-sm text-red-500">{errors.service}</p>}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm md:text-base font-medium text-black/70 mb-2 md:mb-3">
                        What would you like to say? *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full px-4 md:px-6 py-3 md:py-4 bg-black/5 border-2 ${errors.message ? 'border-red-500' : 'border-black/10'} rounded-xl md:rounded-2xl focus:outline-none focus:border-[#FF4E45] transition-all text-base md:text-lg resize-y min-h-[120px] md:min-h-[160px]`}
                        placeholder="Tell us about your project, ideas, or any questions you have..."
                      />
                      {errors.message && <p className="mt-1 md:mt-2 text-xs md:text-sm text-red-500">{errors.message}</p>}
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      onClick={playClick}
                      className="w-full bg-gradient-to-r from-[#FF4E45] to-[#7148FC] text-white px-6 md:px-8 py-4 md:py-5 rounded-xl md:rounded-2xl text-base md:text-lg lg:text-xl font-medium hover:shadow-2xl hover:shadow-[#FF4E45]/30 transition-all duration-300 flex items-center justify-center gap-2 md:gap-3 disabled:opacity-60"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-4 w-4 md:h-5 md:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="h-4 w-4 md:h-5 md:w-5" />
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;
